/**
 * El API expone `deadline_at` como UTC `Y-m-d H:i:s` sin offset, y la UI lo
 * presenta y edita en la zona horaria del equipo. Estas utilidades hacen la
 * conversión en ambos sentidos sin añadir una dependencia de fechas.
 */

/** Desplazamiento en ms de `timeZone` respecto a UTC en el instante `date`. */
function timeZoneOffsetMs(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);

  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0');

  // `hour` puede venir como 24 en la medianoche de algunos entornos.
  const wallClockAsUtc = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour') % 24,
    value('minute'),
    value('second'),
  );

  return wallClockAsUtc - date.getTime();
}

/**
 * Zona del equipo. El setting llega en `/me`; si no está, se usa la del
 * navegador, que para un operario de almacén es la misma.
 */
export function resolveTeamTimeZone(setting: unknown): string {
  if (typeof setting === 'string' && setting.trim() !== '') return setting;
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** `'2026-09-04 16:00:00'` (UTC) → `{ date: '2026-09-04', time: '18:00' }` en Madrid. */
export function utcToTeamFields(utc: string, timeZone: string): { date: string; time: string } | null {
  const match = utc.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return null;

  const [, year, month, day, hours, minutes, seconds = '00'] = match;
  const instant = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes), Number(seconds)),
  );
  if (Number.isNaN(instant.getTime())) return null;

  const local = new Date(instant.getTime() + timeZoneOffsetMs(instant, timeZone));

  return {
    date: local.toISOString().slice(0, 10),
    time: local.toISOString().slice(11, 16),
  };
}

/**
 * `('2026-09-04', '18:00')` en Madrid → `'2026-09-04 16:00:00'` (UTC).
 *
 * ponytail: en la hora que se repite al volver del horario de verano la hora de
 * pared es ambigua (02:30 existe dos veces) y se elige la segunda, ya en horario
 * de invierno. Resolverlo exigiría que el usuario indicara el offset; para una
 * fecha límite de recepción no compensa.
 */
export function teamFieldsToUtc(date: string, time: string, timeZone: string): string | null {
  const dateMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const timeMatch = time.match(/^(\d{2}):(\d{2})$/);
  if (!dateMatch || !timeMatch) return null;

  const [, year, month, day] = dateMatch;
  const [, hours, minutes] = timeMatch;
  const wallClock = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes), 0);
  if (Number.isNaN(wallClock)) return null;

  // El offset depende del instante, y el instante del offset. Una pasada de
  // refinamiento resuelve los saltos de horario de verano.
  let utc = wallClock - timeZoneOffsetMs(new Date(wallClock), timeZone);
  utc = wallClock - timeZoneOffsetMs(new Date(utc), timeZone);

  const iso = new Date(utc).toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 19)}`;
}

/** `'2026-09-04 16:00:00'` (UTC) → `'04/09/2026 18:00'` en la zona del equipo. */
export function formatUtcForDisplay(utc: string | null | undefined, timeZone: string): string {
  if (!utc) return '-';

  const fields = utcToTeamFields(utc, timeZone);
  if (!fields) return '-';

  const [year, month, day] = fields.date.split('-');
  return `${day}/${month}/${year} ${fields.time}`;
}

export const formatDeadlineForDisplay = formatUtcForDisplay;
